import React, { useEffect, useMemo, useState, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, ChevronRight, Search, Truck, Star } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, getServiceProduct, type ServiceCity } from '../data/cityMaterialContent';
import { getRegionMeta } from '../utils/region';
// Sitemap-style index page that links to every generated City × Material
// landing page. The layout splits into a FEATURED row of 3 flagship cities
// (one per region — Pierce home, Seattle flagship, Eastside flagship)
// followed by a compact two-column county-grouped list of the rest. The
// rhythm change keeps the page from feeling like a wall of identical cards.
// Display order of the featured cities at the top of /areas. The filter
// below preserves THIS order (not the order cities appear in
// SERVICE_CITIES) so we can lead with the biggest-market cities.
const FEATURED_SLUGS = ['seattle', 'bellevue', 'tacoma'];
// Subtle zone-driven accent so customers can scan delivery zones across
// the page at a glance. Stays muted — never competes with brand-orange.
// Three delivery tiers: Zone 1 inner (+$80), Zone 2 middle (+$120),
// Zone 3 outer (+$175). Curated city.zone values (legacy 1–5) still map
// onto the correct tier's accent + fee: old 1/2 → inner, old 3 → middle,
// old 4/5 → outer.
const ZONE_ACCENT: Record<number, string> = {
  1: 'border-l-[#059F40]',
  2: 'border-l-[#059F40]',
  3: 'border-l-amber-400',
  4: 'border-l-rose-400',
  5: 'border-l-rose-400'
};
const ZONE_FEE: Record<number, string> = {
  1: '+$80',
  2: '+$80',
  3: '+$120',
  4: '+$175',
  5: '+$175'
};
export function ServiceAreas() {
  const [query, setQuery] = useState('');
  const regionMeta = getRegionMeta();
  const regionSlugs = regionMeta.cities.map(c => c.toLowerCase().replace(/\s+/g, '-'));
  const FEATURED_SLUGS = Array.from(new Set([...regionSlugs, 'seattle', 'bellevue', 'tacoma'])).slice(0, 3);
  useEffect(() => {
    const orig = document.title;
    document.title = 'Service Areas — 253Gravel Delivery to Tacoma, Seattle, Bellevue, Puyallup & more';
    return () => {
      document.title = orig;
    };
  }, []);
  const products = useMemo(() => SERVICE_PRODUCT_SLUGS.map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[], []);
  const matches = (city: ServiceCity) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return city.name.toLowerCase().includes(q) || city.county.toLowerCase().includes(q) || city.neighborhoods.some(n => n.toLowerCase().includes(q));
  };
  // Map over FEATURED_SLUGS (not filter on SERVICE_CITIES) so the
  // display order respects the curated lead-with list above.
  const featured = FEATURED_SLUGS.map(slug => SERVICE_CITIES.find(c => c.slug === slug)).filter(Boolean) as typeof SERVICE_CITIES;
  const featuredVisible = featured.filter(matches);
  // Non-featured grouped by county for the secondary section.
  const remainingByCounty = useMemo(() => {
    const remaining = SERVICE_CITIES.filter(c => !FEATURED_SLUGS.includes(c.slug) && matches(c));
    return {
      King: remaining.filter(c => c.county === 'King'),
      Pierce: remaining.filter(c => c.county === 'Pierce')
    };
  }, [query]);
  const totalMatches = featuredVisible.length + remainingByCounty.King.length + remainingByCounty.Pierce.length;
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-3139">
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-3140">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-3141">
          <li data-id="element-3142">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3143">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-3144" />
          <li className="text-stone-900 dark:text-white font-semibold" aria-current="page" data-id="element-3145">
            Service Areas
          </li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-8" data-id="element-3146">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-3147">
          <div className="inline-flex items-center gap-2 mb-3" data-id="element-3148">
            <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-3149" />
            <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-3150">
              South Sound · King & Pierce County
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-3151">
            Where we deliver
          </h1>
          <p className="mt-4 text-stone-600 dark:text-stone-300 max-w-3xl" data-id="element-3152">
            We deliver gravel, topsoil, bark, and drain rock across South King
            and Pierce County. Tap a city to see pricing, materials, and
            delivery details specific to your neighborhood.
          </p>
        </motion.div>

        {/* Search + zone legend */}
        <div className="mt-6 grid md:grid-cols-[1fr,auto] gap-4 items-center" data-id="element-3153">
          <div className="relative" data-id="element-3154">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" data-id="element-3155" />
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search a city or neighborhood…" className="w-full pl-10 pr-4 py-3 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-sm text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-orange" data-id="element-3156" />
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-bold text-stone-600 dark:text-stone-300" data-id="element-3157">
            <span className="uppercase tracking-wider text-stone-400 dark:text-stone-500" data-id="element-3158">
              Zone fees
            </span>
            {([1, 2, 3, 4] as const).map(z => <span key={z} className="inline-flex items-center gap-1.5" data-id="element-3159">
                <span className={`block w-2 h-2 rounded-full ${ZONE_ACCENT[z].replace('border-l-', 'bg-')}`} data-id="element-3160" />
                Z{z} {ZONE_FEE[z]}
              </span>)}
          </div>
        </div>
      </section>

      {/* Featured cities */}
      {featuredVisible.length > 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10" data-id="element-3161">
          <div className="flex items-center gap-2 mb-4" data-id="element-3162">
            <Star className="w-4 h-4 text-brand-orange" data-id="element-3163" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-brand-orange" data-id="element-3164">
              Featured cities
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4" data-id="element-3165">
            {featuredVisible.map((city, idx) => <motion.div key={city.slug} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.05 * idx
        }} id={city.slug} className={`scroll-mt-24 p-6 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 border-l-4 ${ZONE_ACCENT[city.zone]}`} data-id="element-3166">
                <div className="flex items-start justify-between gap-3 mb-4" data-id="element-3167">
                  <div data-id="element-3168">
                    <h3 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-3169">
                      {city.name}
                    </h3>
                    <p className="mt-1 text-[11px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-3170">
                      {city.county} County · Zone {city.zone}
                    </p>
                  </div>
                  <span className="px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[11px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-3171">
                    {ZONE_FEE[city.zone]}
                  </span>
                </div>

                <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed mb-4" data-id="element-3172">
                  {city.blurb}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-5" data-id="element-3173">
                  {city.neighborhoods.slice(0, 4).map(n => <span key={n} className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-medium text-stone-600 dark:text-stone-400" data-id="element-3174">
                      {n}
                    </span>)}
                  {city.neighborhoods.length > 4 && <span className="px-2 py-0.5 text-[10px] font-medium text-stone-400" data-id="element-3175">
                      +{city.neighborhoods.length - 4} more
                    </span>}
                </div>

                <div className="grid grid-cols-2 gap-1.5 mb-4" data-id="element-3176">
                  {products.map(({
              product
            }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="group px-3 py-2 rounded-lg bg-stone-50 dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-3177">
                      <div className="text-xs font-bold text-stone-900 dark:text-white truncate group-hover:text-brand-orange transition-colors" data-id="element-3178">
                        {product.name}
                      </div>
                      <div className="text-[10px] text-stone-500 dark:text-stone-400" data-id="element-3179">
                        ${product.pricePerYard}/yd
                      </div>
                    </Link>)}
                </div>

                <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange hover:underline" data-id="element-3180">
                  See {city.name} on the zone map
                  <ArrowRight className="w-3 h-3" data-id="element-3181" />
                </Link>
              </motion.div>)}
          </div>
        </section>}

      {/* More service areas — county-grouped */}
      {(remainingByCounty.Pierce.length > 0 || remainingByCounty.King.length > 0) && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3182">
          <div className="flex items-center gap-2 mb-4" data-id="element-3183">
            <Truck className="w-4 h-4 text-stone-500 dark:text-stone-400" data-id="element-3184" />
            <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-stone-500 dark:text-stone-400" data-id="element-3185">
              More service areas
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-3186">
            {(['Pierce', 'King'] as const).map(county => {
          const list = remainingByCounty[county];
          if (list.length === 0) return null;
          return <div key={county} data-id="element-3187">
                  <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-3" data-id="element-3188">
                    {county} County
                    <span className="ml-2 text-xs font-normal text-stone-400" data-id="element-3189">
                      {list.length} cit{list.length === 1 ? 'y' : 'ies'}
                    </span>
                  </h3>
                  <div className="space-y-3" data-id="element-3190">
                    {list.map(city => <div key={city.slug} id={city.slug} className={`scroll-mt-24 p-4 rounded-xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 border-l-4 ${ZONE_ACCENT[city.zone]}`} data-id="element-3191">
                        <div className="flex items-center justify-between gap-3 mb-2" data-id="element-3192">
                          <div data-id="element-3193">
                            <p className="font-heading text-base font-bold text-stone-900 dark:text-white" data-id="element-3194">
                              {city.name}
                            </p>
                            <p className="text-[10px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider" data-id="element-3195">
                              Zone {city.zone} · {city.neighborhoods.length}+
                              neighborhoods
                            </p>
                          </div>
                          <span className="px-2 py-0.5 rounded-full bg-stone-100 dark:bg-white/[0.06] text-[10px] font-bold text-stone-700 dark:text-stone-200 shrink-0" data-id="element-3196">
                            {ZONE_FEE[city.zone]}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5" data-id="element-3197">
                          {products.map(({
                    product
                  }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="inline-flex items-center px-2.5 py-1 rounded-full bg-stone-100 dark:bg-white/[0.06] text-xs font-bold text-stone-700 dark:text-stone-200 hover:bg-brand-orange/10 hover:text-brand-orange transition-colors" data-id="element-3198">
                              {product.name}
                            </Link>)}
                        </div>
                      </div>)}
                  </div>
                </div>;
        })}
          </div>
        </section>}

      {/* Empty state */}
      {totalMatches === 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3199">
          <div className="p-10 rounded-2xl bg-white dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 text-center" data-id="element-3200">
            <p className="text-sm text-stone-700 dark:text-stone-300" data-id="element-3201">
              No service areas match "{query}". Try a different city or
              neighborhood.
            </p>
            <button type="button" onClick={() => setQuery('')} className="mt-3 text-xs font-bold text-brand-orange hover:underline" data-id="element-3202">
              Clear search
            </button>
          </div>
        </section>}

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-3203">
        <div className="p-6 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 text-center" data-id="element-3204">
          <h2 className="font-heading text-xl font-bold text-white" data-id="element-3205">
            Not seeing your city?
          </h2>
          <p className="mt-2 text-sm text-stone-300" data-id="element-3206">
            We deliver well beyond these cities. Check the zone map or call us
            for a custom quote.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3" data-id="element-3207">
            <Link to="/delivery-zones" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-3208">
              View Zone Map
              <ArrowRight className="w-3.5 h-3.5" data-id="element-3209" />
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-3210">
              (206) 619-3742
            </a>
          </div>
        </div>
      </section>
    </main>;
}